// Choices stay on this page until the main product form is submitted.
import { Component } from '@theme/component';
import { offerTotal } from '@theme/ma-offer-price';

class MerseyProductCapSelector extends Component {
  selected = new Map();
  busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.variants = JSON.parse(this.querySelector('[data-main-variants]').textContent);
    this.target = Number(this.dataset.target) || 3;
    this.productForm = this.closest('product-form-component');
    this.rail = this.querySelector('.ma-cap-set__rail');
    this.addEventListener('click', this.onClick);
    this.rail?.addEventListener('scroll', this.updateArrows, { passive: true });
    this.productForm?.addEventListener('change', this.update);
    this.productForm?.addEventListener('quantity-selector:update', this.update);
    this.resizeObserver = new ResizeObserver(this.updateArrows);
    if (this.rail) this.resizeObserver.observe(this.rail);
    this.hidden = false;
    this.update();
  }
  updatedCallback() {
    super.updatedCallback();
    this.update();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.onClick);
    this.rail?.removeEventListener('scroll', this.updateArrows);
    this.productForm?.removeEventListener('change', this.update);
    this.productForm?.removeEventListener('quantity-selector:update', this.update);
    this.resizeObserver?.disconnect();
  }
  getSelectedItems() {
    return Array.from(this.selected.keys(), variantId => ({ variantId, quantity: 1 }));
  }
  getSelectionSummary() {
    const quantity = Number(this.productForm?.querySelector('input[name="quantity"]')?.value) || 1;
    const count = quantity + this.selected.size;
    return {
      active: this.selected.size > 0,
      button: this.dataset.button.replace('[count]', String(count)),
      summary: this.dataset.stickySummary.replace('[count]', String(count))
    };
  }
  setBusy(value) {
    this.busy = value;
    this.setAttribute('aria-busy', String(value));
    this.querySelectorAll('button[data-cap-variant], button[data-remove-cap]').forEach(button => { button.disabled = value; });
    if (!value) this.update();
  }
  reset() {
    this.selected.clear();
    this.update();
  }
  updateArrows = () => {
    if (!this.rail) return;
    this.querySelector('[data-cap-direction="-1"]').disabled = this.rail.scrollLeft <= 2;
    this.querySelector('[data-cap-direction="1"]').disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 2;
  };
  onClick = event => {
    if (!(event.target instanceof Element)) return;
    const arrow = event.target.closest('[data-cap-direction]');
    if (arrow) {
      this.rail.scrollBy({ left: Number(arrow.dataset.capDirection) * (this.rail.clientWidth - 4), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      return;
    }
    const button = event.target.closest('button[data-cap-variant],button[data-remove-cap]');
    if (!button || this.busy) return;
    const id = button.dataset.capVariant || button.dataset.removeCap;
    if (this.selected.has(id)) this.selected.delete(id);
    else if (this.selected.size < Math.max(0, this.target - (Number(this.productForm?.querySelector('input[name="quantity"]')?.value) || 1))) this.selected.set(id, { title: button.dataset.title, price: Number(button.dataset.price), image: button.dataset.image });
    else {
      this.querySelector('.ma-cap-set__status').textContent = this.dataset.limit;
      return;
    }
    const removedChip = button.hasAttribute('data-remove-cap');
    this.update();
    if (removedChip) {
      const focusTarget = this.querySelector('details')?.open
        ? this.querySelector(`button[data-cap-variant="${id}"]`)
        : this.querySelector('summary');
      focusTarget?.focus({ preventScroll: true });
    }
  };
  update = () => {
    const quantity = Number(this.productForm?.querySelector('input[name="quantity"]')?.value) || 1;
    const remaining = Math.max(0, this.target - quantity);
    while (this.selected.size > remaining) this.selected.delete([...this.selected.keys()].at(-1));
    const count = quantity + this.selected.size;
    const mainId = Number(this.productForm?.querySelector('input[name="id"]')?.value);
    const main = this.variants.find(item => item.id === mainId) || this.variants[0];
    const slots = this.querySelector('.ma-cap-set__slots');
    slots.replaceChildren();
    const chosen = [...Array(Math.min(quantity, this.target)).fill({...main, title: this.dataset.currentLabel}), ...Array.from(this.selected, ([id, item]) => ({...item, id}))];
    for (let i = 0; i < this.target; i++) {
      const slot = document.createElement('li');
      slot.className = 'ma-cap-set__slot';
      const item = chosen[i];
      slot.dataset.filled = String(Boolean(item));
      const number = document.createElement('span');
      number.className = 'ma-cap-set__slot-number';
      number.textContent = this.dataset.slotLabel.replace('[number]', String(i + 1));
      slot.append(number);
      if (item && i >= quantity) {
        const remove = document.createElement('button');
        remove.type = 'button'; remove.dataset.removeCap = item.id;
        remove.textContent = '×'; remove.disabled = this.busy;
        remove.setAttribute('aria-label', this.dataset.removeLabel.replace('[title]', item.title));
        slot.append(remove);
      }
      if (item?.image) {
        const img = document.createElement('img');
        img.src = item.image; img.alt = ''; img.width = 42; img.height = 42;
        slot.append(img);
      }
      const name = document.createElement('span');
      name.textContent = item?.title || this.dataset.emptyLabel;
      slot.append(name); slots.append(slot);
    }
    const total = this.querySelector('.ma-cap-set__total');
    total.hidden = this.selected.size === 0 && quantity < this.target;
    if (!total.hidden) {
      const prices = [...Array(Math.min(quantity, 1000)).fill(main.price), ...Array.from(this.selected.values(), item => item.price)];
      const amount = offerTotal(prices, this.dataset.offer);
      const label = prices.length >= this.target ? this.dataset.totalLabel : this.dataset.estimateLabel;
      total.textContent = label + ' ' + new Intl.NumberFormat(this.dataset.locale, {style:'currency',currency:this.dataset.currency}).format(amount / 100);
    }
    this.querySelector('.ma-cap-set__price-note').hidden = total.hidden;
    this.querySelectorAll('button[data-cap-variant]').forEach(button => {
      const chosen = this.selected.has(button.dataset.capVariant);
      button.disabled = this.busy || remaining === 0;
      button.setAttribute('aria-pressed', String(chosen));
      button.textContent = chosen ? this.dataset.selected : this.dataset.select;
      button.setAttribute('aria-label', (chosen ? this.dataset.removeLabel : this.dataset.selectLabel).replace('[title]', button.dataset.title));
    });
    this.querySelector('.ma-cap-set__status').textContent = count >= this.target ? this.dataset.ready : this.selected.size ? this.dataset.summary.replace('[count]', String(count)) : this.dataset.instruction;
    const selection = this.querySelector('.ma-cap-set__selection');
    selection.replaceChildren();
    selection.hidden = this.selected.size === 0;
    for (const [id, item] of this.selected) {
      const title = item.title;
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.dataset.removeCap = id;
      const name = document.createElement('span');
      name.className = 'ma-cap-set__selection-name';
      name.textContent = title;
      const remove = document.createElement('span');
      remove.className = 'ma-cap-set__selection-remove';
      remove.textContent = '×';
      remove.setAttribute('aria-hidden', 'true');
      chip.append(name, remove);
      chip.title = title;
      chip.setAttribute('aria-label', this.dataset.removeLabel.replace('[title]', title));
      chip.disabled = this.busy;
      selection.append(chip);
    }
    const addButton = this.productForm?.querySelector('button[name="add"]');
    if (addButton && !addButton.disabled) {
      // Preserve the native icon and confirmation animation while changing only its text node.
      const label = Array.from(addButton.childNodes).find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (label) {
        if (!this.defaultLabel) this.defaultLabel = label.textContent;
        const text = this.selected.size ? this.dataset.button.replace('[count]', String(count)) : this.defaultLabel;
        if (label.textContent !== text) label.textContent = text;
      }
    }
    this.dispatchEvent(new CustomEvent('ma:cap-selection-change', { bubbles: true }));
    this.updateArrows();
  };
}
if (!customElements.get('ma-product-cap-selector')) {
  customElements.define('ma-product-cap-selector', MerseyProductCapSelector);
}

